import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import NodeSkeleton from "./NodeSkeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import React, { memo } from "react";
import { Handle, Position, type NodeProps } from "@xyflow/react";
import { useTheme } from "@mui/material/styles";
import type { ExplorerNode } from "../../declarations/IExplorerTypes";
import { getNodeColor, getNodeIcon } from "./nodeColors";
import { getNodeCardSx } from "./nodeCardStyles";
import NodeToolbar from "./NodeToolbar";

function OperationDetailNode({ data }: NodeProps<ExplorerNode>) {
    const theme = useTheme();
    const prop = data.operationProperty;
    const nodeColor = getNodeColor(data.entityType);
    const isDark = theme.palette.mode === "dark";
    const isLoading = data.status === "loading";

    return (
        <>
            <Handle type="target" position={Position.Left} style={{ opacity: 0 }} />
            <Card
                elevation={0}
                sx={getNodeCardSx({ nodeColor, isSelected: data.isSelected, isDark, width: 220, source: data.source })}
            >
                <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                    <NodeToolbar
                        nodeColor={nodeColor}
                        icon={getNodeIcon(data.entityType)}
                        title={data.nodeTitle}
                        subtitle={data.nodeSubtitle}
                        dataAreaId={data.dataAreaId}
                        onInfoClick={data.onSelect ? () => data.onSelect!(data.selectId ?? data.entityType) : undefined}
                    />

                    {isLoading ? (
                        <NodeSkeleton rows={2} variant="detail" />
                    ) : !prop ? (
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            No properties found
                        </Typography>
                    ) : (
                        <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                            <Stack
                                direction="row"
                                sx={{
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: "text.secondary",
                                        opacity: 0.8
                                    }}>Site</Typography>
                                <Typography variant="caption" sx={{
                                    fontWeight: 500
                                }}>{prop.productionSiteId}</Typography>
                            </Stack>
                            <Stack
                                direction="row"
                                sx={{
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: "text.secondary",
                                        opacity: 0.8
                                    }}>Process Time</Typography>
                                <Typography variant="caption" sx={{
                                    fontWeight: 500
                                }}>{prop.processTime}h</Typography>
                            </Stack>
                        </Stack>
                    )}
                </CardContent>
            </Card>
            <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />
        </>
    );
}

export default memo(OperationDetailNode);
